import { useCallback } from "react";
import Swal, { SweetAlertIcon, SweetAlertOptions } from "sweetalert2";

export interface AlertOptions {
  title?: string;
  text?: string;
  html?: string;
  icon?: SweetAlertIcon;
  confirmButtonText?: string;
  cancelButtonText?: string;
  showCancelButton?: boolean;
  timer?: number;
  timerProgressBar?: boolean;
  position?:
    | "top"
    | "top-start"
    | "top-end"
    | "center"
    | "center-start"
    | "center-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end";
  toast?: boolean;
  showConfirmButton?: boolean;
  customClass?: {
    container?: string;
    popup?: string;
    header?: string;
    title?: string;
    closeButton?: string;
    icon?: string;
    image?: string;
    content?: string;
    htmlContainer?: string;
    input?: string;
    inputLabel?: string;
    validationMessage?: string;
    actions?: string;
    confirmButton?: string;
    denyButton?: string;
    cancelButton?: string;
    loader?: string;
    footer?: string;
    timerProgressBar?: string;
  };
}

export const useSweetAlert = () => {
  const showAlert = useCallback((options: SweetAlertOptions) => {
    return Swal.fire(options);
  }, []);

  const showSuccess = useCallback(
    (title: string, text?: string, options?: AlertOptions) => {
      return Swal.fire({
        icon: "success",
        title,
        text,
        confirmButtonColor: "#10b981",
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-lg px-6 py-3 font-medium",
        },
        ...options,
      });
    },
    []
  );

  const showError = useCallback(
    (title: string, text?: string, options?: AlertOptions) => {
      return Swal.fire({
        icon: "error",
        title,
        text,
        confirmButtonColor: "#ef4444",
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-lg px-6 py-3 font-medium",
        },
        ...options,
      });
    },
    []
  );

  const showWarning = useCallback(
    (title: string, text?: string, options?: AlertOptions) => {
      return Swal.fire({
        icon: "warning",
        title,
        text,
        confirmButtonColor: "#f59e0b",
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-lg px-6 py-3 font-medium",
        },
        ...options,
      });
    },
    []
  );

  const showInfo = useCallback(
    (title: string, text?: string, options?: AlertOptions) => {
      return Swal.fire({
        icon: "info",
        title,
        text,
        confirmButtonColor: "#3b82f6",
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-lg px-6 py-3 font-medium",
        },
        ...options,
      });
    },
    []
  );

  const showQuestion = useCallback(
    (title: string, text?: string, options?: AlertOptions) => {
      return Swal.fire({
        icon: "question",
        title,
        text,
        showCancelButton: true,
        confirmButtonColor: "#3b82f6",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-lg px-6 py-3 font-medium",
          cancelButton: "rounded-lg px-6 py-3 font-medium",
        },
        ...options,
      });
    },
    []
  );

  const showToast = useCallback(
    (
      icon: SweetAlertIcon,
      title: string,
      position: AlertOptions["position"] = "top-end",
      timer: number = 3000
    ) => {
      return Swal.fire({
        toast: true,
        position,
        icon,
        title,
        showConfirmButton: false,
        timer,
        timerProgressBar: true,
        customClass: {
          popup: "rounded-lg shadow-lg",
          timerProgressBar: "bg-gradient-to-r from-blue-500 to-purple-600",
        },
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });
    },
    []
  );

  const showConfirm = useCallback(
    (
      title: string,
      text?: string,
      confirmText: string = "Yes, do it!",
      cancelText: string = "Cancel"
    ) => {
      return Swal.fire({
        title,
        text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-lg px-6 py-3 font-medium",
          cancelButton: "rounded-lg px-6 py-3 font-medium",
        },
      });
    },
    []
  );

  const showInput = useCallback(
    (
      title: string,
      inputPlaceholder: string = "Enter your input...",
      inputType:
        | "text"
        | "email"
        | "password"
        | "number"
        | "tel"
        | "range"
        | "textarea"
        | "select"
        | "radio"
        | "checkbox"
        | "file"
        | "url" = "text"
    ) => {
      return Swal.fire({
        title,
        input: inputType,
        inputPlaceholder,
        showCancelButton: true,
        confirmButtonColor: "#3b82f6",
        cancelButtonColor: "#6b7280",
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-lg px-6 py-3 font-medium",
          cancelButton: "rounded-lg px-6 py-3 font-medium",
          input:
            "rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500",
        },
        inputValidator: (value: any) => {
          if (!value) {
            return "You need to write something!";
          }
          if (inputType === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              return "Please enter a valid email address.";
            }
          }
        },
      });
    },
    []
  );

  const showLoading = useCallback(
    (title: string = "Loading...", text?: string) => {
      return Swal.fire({
        title,
        text,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        customClass: {
          popup: "rounded-2xl shadow-2xl",
        },
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    []
  );

  const closeAlert = useCallback(() => {
    Swal.close();
  }, []);

  return {
    showAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showQuestion,
    showToast,
    showConfirm,
    showInput,
    showLoading,
    closeAlert,
  };
};
